/* eslint-disable import/no-default-export */
// eslint-disable-next-line no-restricted-imports
import Spinner from 'common/components/loading/spinner';
import Keybindings, { Key } from 'app/gamma/src/components/shared/keybindings';
import React from 'react';
// eslint-disable-next-line no-restricted-imports
import {
  BoardModel,
  BoardsMenuCategoryModel,
  BoardsMenuSelectedItemModel,
} from 'types/models';
// eslint-disable-next-line no-restricted-imports
import preventDefault from 'util/prevent-default';

import BoardList from './board-list';
import Popover from './boards-menu-popover';
import styles from './boards-menu.less';
import BoardsSearch from './boards-search';

const BOARDS_MENU_WIDTH = 280;
const POPOVER_TOP_OFFSET = 44; // The height of the header + 3px

import { HeaderTestIds } from '@trello/webdriverio-tests';
// eslint-disable-next-line no-restricted-imports
import { forNamespace, forTemplate } from 'common/lib/util/i18n';
// eslint-disable-next-line no-restricted-imports
import {
  setBoardsMenuSearchText,
  setBoardsMenuSelectedBoard,
} from 'modules/state/ui/boards-menu';
import { connect, Dispatch } from 'react-redux';
// eslint-disable-next-line no-restricted-imports
import { closePopover } from 'modules/state/ui/popovers';

const format = forTemplate('boards_sidebar');
const formatRaw = forNamespace();

export const POPOVER_NAME = Symbol('POPOVER_BOARDS_MENU');
interface BoardsMenuPopoverProps {
  readonly alwaysOpenBoardsMenu: boolean;
  readonly categoriesWithBoards: BoardsMenuCategoryModel[];
  readonly isBoardsMenuPopoverOpen: boolean;
  readonly isLoggedIn: boolean;
  readonly isSearching: boolean;
  readonly loading: boolean;
  readonly scrollPosition: number;
  readonly searchText: string;
  readonly selectedBoard: BoardsMenuSelectedItemModel | null;
  readonly filteredBoards: BoardModel[] | null;
  readonly visibleBoards: BoardsMenuSelectedItemModel[] | null;
  readonly setBoardsMenuScrollPosition: (position: number) => void;
  readonly onClose: (trackingMethod: string) => void;
  readonly showCreateBoard: () => void;
  readonly toggleAlwaysOpenBoardsMenu: (alwaysOpenBoardsMenu: boolean) => void;
  readonly toggleClosedBoardDialog: () => void;
  readonly setSelectedBoard: (
    selected: BoardsMenuSelectedItemModel | null,
  ) => void;
}

interface BoardsMenuStateProps {
  dispatch: Dispatch<object>;
}

type BoardsMenuProps = BoardsMenuPopoverProps & BoardsMenuStateProps;

class BoardsMenu extends React.Component<BoardsMenuProps> {
  ref = React.createRef<HTMLDivElement>();
  searchRef = React.createRef<HTMLInputElement>();

  componentDidMount() {
    const { alwaysOpenBoardsMenu, isBoardsMenuPopoverOpen } = this.props;
    if (alwaysOpenBoardsMenu) {
      this.onShowBoardsMenu();
    } else if (isBoardsMenuPopoverOpen) {
      this.onShowBoardsMenu();
    }
  }

  componentDidUpdate(prevProps: BoardsMenuProps) {
    // toggled the boards popover open
    if (
      !this.props.alwaysOpenBoardsMenu &&
      this.props.isBoardsMenuPopoverOpen &&
      !prevProps.isBoardsMenuPopoverOpen
    ) {
      this.onShowBoardsMenu();

      // toggled the boards popover closed
    } else if (
      !this.props.alwaysOpenBoardsMenu &&
      !this.props.isBoardsMenuPopoverOpen &&
      prevProps.isBoardsMenuPopoverOpen
    ) {
      this.onHideBoardsMenu();
    }
  }

  componentWillUnmount() {
    this.onHideBoardsMenu();
  }

  private onFocusInputKeypress = (e: React.KeyboardEvent<HTMLElement>) => {
    if (
      this.props.alwaysOpenBoardsMenu &&
      this.searchRef &&
      this.searchRef.current
    ) {
      this.searchRef.current.focus();
      e.preventDefault();
    }
  };

  private onShowBoardsMenu() {
    // When the BoardsMenu is mounted, the <input> within the <Filter>
    // component is focused, which causes the browser to scroll the
    // menu down a bit.
    // Also, we need to keep the scroll position when switching between
    // "always open" mode
    const { current } = this.ref;
    const {
      alwaysOpenBoardsMenu,
      scrollPosition,
      isBoardsMenuPopoverOpen,
    } = this.props;

    if (!alwaysOpenBoardsMenu && !isBoardsMenuPopoverOpen) {
      return;
    }

    if (current && current.parentElement) {
      const scrollElement = alwaysOpenBoardsMenu
        ? current
        : current.parentElement;
      setImmediate(() => {
        scrollElement.scrollTop = scrollPosition;
      });
    }

    setTimeout(() => {
      if (this.searchRef && this.searchRef.current) {
        this.searchRef.current.focus();
      }
    });
  }

  // Since we're not unmounting when we toggle the visible state,
  // of the popover, we need to reset this state
  // to it's defaults when hiding the boards menu
  private onHideBoardsMenu() {
    setTimeout(() => {
      if (this.searchRef && this.searchRef.current) {
        this.searchRef.current.value = '';
      }
      this.props.dispatch(setBoardsMenuSearchText(''));
      this.props.dispatch(setBoardsMenuSelectedBoard(null));
    });
  }

  keybindings = {
    [Key.b]: {
      callback: this.onFocusInputKeypress,
      isGlobal: true,
    },
  };

  render() {
    const {
      alwaysOpenBoardsMenu,
      categoriesWithBoards,
      filteredBoards,
      selectedBoard,
      isSearching,
      isBoardsMenuPopoverOpen,
      loading,
      scrollPosition,
      setBoardsMenuScrollPosition,
      searchText,
      showCreateBoard,
      toggleAlwaysOpenBoardsMenu,
      toggleClosedBoardDialog,
      visibleBoards,
      dispatch,
    } = this.props;

    return (
      <div
        ref={this.ref}
        className={alwaysOpenBoardsMenu ? styles.popoverStandalone : ''}
        data-test-id={HeaderTestIds.BoardsMenuPopover}
      >
        <Keybindings keybindings={this.keybindings}>
          <BoardsSearch
            ref={this.searchRef}
            boards={filteredBoards}
            searchText={searchText}
            selectedBoard={selectedBoard}
            focusInput={!alwaysOpenBoardsMenu}
            visibleBoards={visibleBoards}
            isBoardsMenuPopoverOpen={isBoardsMenuPopoverOpen}
            closePopover={() => dispatch(closePopover(POPOVER_NAME))}
          />
        </Keybindings>
        <BoardList
          categories={categoriesWithBoards}
          hidden={isSearching || loading}
        />
        {loading && !scrollPosition && <Spinner>{format('boards')}</Spinner>}
        <div className={styles.buttons}>
          <button
            onClick={showCreateBoard}
            className={styles.quietButton}
            data-test-id={HeaderTestIds.BoardsMenuCreateBoard}
          >
            {isSearching
              ? formatRaw('add board named', { boardName: searchText })
              : format('create-new-board-ellipsis')}
          </button>
          {!isSearching && (
            <>
              <button
                onClick={preventDefault(() => {
                  setBoardsMenuScrollPosition(
                    alwaysOpenBoardsMenu
                      ? this.ref.current!.scrollTop
                      : this.ref.current!.parentElement!.scrollTop,
                  );
                  toggleAlwaysOpenBoardsMenu(alwaysOpenBoardsMenu);
                })}
                className={styles.quietButton}
                data-test-id={HeaderTestIds.BoardsMenuPin}
              >
                {!alwaysOpenBoardsMenu
                  ? format('always-keep-this-menu-open')
                  : format('don-t-keep-this-menu-open')}
              </button>
              <button
                onClick={toggleClosedBoardDialog}
                className={styles.quietButton}
                data-test-id={HeaderTestIds.BoardsMenuOpenClosed}
              >
                {format('see-closed-boards-ellipsis')}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }
}

class BoardsMenuPopover extends React.Component<BoardsMenuProps> {
  // Reset selection state so it doesn't get persisted across
  // closing and reopening the popover
  componentDidMount() {
    this.props.setSelectedBoard(null);
  }

  render() {
    const {
      isLoggedIn,
      alwaysOpenBoardsMenu,
      isBoardsMenuPopoverOpen,
    } = this.props;

    if (isLoggedIn && alwaysOpenBoardsMenu) {
      return (
        <div className={styles.boardsDrawerContainer}>
          <div className={styles.boardsDrawerHeader}>{format('boards')}</div>
          <div className={styles.boardsMenu}>
            <BoardsMenu {...this.props} />
          </div>
        </div>
      );
    }

    return (
      <Popover
        disableAutoFocus
        className={styles.popover}
        hidden={!isBoardsMenuPopoverOpen}
        contentClassName={styles.popoverContent}
        left={0}
        name={POPOVER_NAME}
        top={POPOVER_TOP_OFFSET}
        width={BOARDS_MENU_WIDTH}
        onClose={this.props.onClose}
      >
        {() => <BoardsMenu {...this.props} />}
      </Popover>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<object>) => ({
  dispatch,
});

export default connect(
  undefined,
  mapDispatchToProps,
)(BoardsMenuPopover);



// WEBPACK FOOTER //
// ./app/gamma/src/components/popovers/boards-menu/boards-menu.tsx